import React, {
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useRef,
  useState,
  Fragment,
  useEffect,
} from "react";
import Fullscreen from "./fullscreen";
import Overlay from "./overlay";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import useBodyClass from "../../../shared/use_body_class";
import { AppContext } from "../../../app";

export enum ModalType {
  fullscreen,
  overlay,
}

const ModalContext = createContext<RefObject<HTMLDivElement> | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [currentRef, setRef] = useState<RefObject<HTMLDivElement> | null>(null);

  useEffect(() => {
    setRef(ref);
  }, []);

  return (
    <Fragment>
      <ModalContext.Provider value={currentRef}>{children}</ModalContext.Provider>
      <div ref={ref} />
    </Fragment>
  );
}

// sometimes the modal can't hide overflow, so make sure the top of the header is always rounded (for overlay)
const Header = styled.header<{ isOverlay: boolean }>`
  ${({ isOverlay, theme }) =>
    isOverlay ? `border-top-left-radius: ${theme.deprecatedBorder.radius.large}` : ""};
  ${({ isOverlay, theme }) =>
    isOverlay ? `border-top-right-radius: ${theme.deprecatedBorder.radius.large}` : ""};
  background-color: ${({ theme }) => theme.deprecatedColors.white};
  padding-left: ${({ theme }) => theme.deprecatedSpacing.component.five};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  place-items: center;
  box-shadow: ${({ theme }) => theme.deprecatedElevation.one};
  position: relative;
  ${({ isOverlay }) => (isOverlay ? "height: 35px;" : "")};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.modal + 1};
`;

const CloseButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  margin-left: auto;
  padding: ${({ theme }) => theme.deprecatedSpacing.component.six};
  font-size: ${({ theme }) => theme.font.size.header.four};
  color: ${({ theme }) => theme.deprecatedColors.gray.seven};
`;

const ModalOverlay = styled.div`
  z-index: ${({ theme }) => theme.zIndex.modal};
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  overflow-x: hidden;

  background-color: rgba(0, 0, 0, 0.4);

  .modal-wrapper {
    position: relative;
    width: auto;
    display: flex;
    align-items: center;
    min-height: 100%;
    overflow-y: auto;
    max-height: calc(100vh - 300px);
  }
`;

export default function Modal({
  children,
  modalType = ModalType.fullscreen,
  onClickExit,
  headerContent,
  className,
  dataTest,
}: {
  children: ReactNode;
  modalType?: number;
  headerContent?: ReactNode;
  onClickExit: () => void;
  className?: string;
  dataTest?: string;
}) {
  useBodyClass("disable-scrolling");
  let StyledModal = Fullscreen;

  switch (modalType) {
    case ModalType.fullscreen:
      StyledModal = Fullscreen;
      break;
    case ModalType.overlay:
      StyledModal = Overlay;
      break;
    default:
      StyledModal = Fullscreen;
      break;
  }

  const modalRef = useContext(ModalContext);
  const { envBanner } = useContext(AppContext);

  return modalRef && modalRef.current
    ? createPortal(
        <ModalOverlay className={className}>
          <div className="modal-wrapper">
            <StyledModal
              className="modal"
              data-test={dataTest || "modal"}
              showEnvBanner={!!envBanner}
            >
              <Header isOverlay={modalType === ModalType.overlay}>
                {headerContent}
                <CloseButton onClick={onClickExit} aria-label="Close">
                  <Icon icon="times" />
                </CloseButton>
              </Header>
              {children}
            </StyledModal>
          </div>
        </ModalOverlay>,
        modalRef.current,
      )
    : null;
}

export const StyledConfirmationModal = styled.div`
  padding: ${({ theme }) => theme.deprecatedSpacing.component.six};
  position: relative;

  .buttons {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    right: ${({ theme }) => theme.deprecatedSpacing.component.six};
    bottom: ${({ theme }) => theme.deprecatedSpacing.component.six};

    button {
      margin-left: ${({ theme }) => theme.deprecatedSpacing.component.six};
      border-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
    }
  }
`;
